import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

const useStyles = makeStyles(
  /* istanbul ignore next */ ({ spacing, palette, typography, shape }) => ({
    root: {
      position: 'relative',
      width: '100%',
    },

    input: {
      width: '100%',
      border: `1px solid ${palette.layout.line.divider}`,
      padding: spacing(1, 5, 1, 1),
      boxSizing: 'border-box',
      borderRadius: shape.borderRadius,
      ...typography.body1,
      lineHeight: ['1.4rem', '!important'],
      fontSize: ['1rem', '!important'],
    },

    searchIcon: {
      position: 'absolute',
      top: spacing(1),
      right: spacing(1),
    },

    resetIcon: {
      position: 'absolute',
      color: palette.text.onMain,
      top: -3,
      right: -1,
    },
  })
);

export default function SearchField({
  onChange = () => {},
  hasSearchIconHidden = false,
  shouldAutoFocus = false,
  translations,
  ...otherProps
}) {
  const classes = useStyles();
  const [value, setValue] = useState('');
  const inputElement = useRef(null);
  const timeout = useRef(null);

  const onThrottledChange = (event) => {
    const { value: newValue } = event.target;
    setValue(newValue);

    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    timeout.current = setTimeout(() => {
      timeout.current = null;
      onChange(newValue);
    }, 250);
  };

  useEffect(() => {
    if (!inputElement || !shouldAutoFocus) {
      return;
    }
    inputElement.current.focus();
  }, [inputElement]);

  const decorativeSearchIcon = hasSearchIconHidden ? null : (
    <SearchIcon className={classes.searchIcon} data-testid="visual-icon" />
  );

  return (
    <div className={classes.root}>
      <input
        ref={inputElement}
        value={value}
        placeholder={translations.inputSearchPlaceholder}
        onChange={onThrottledChange}
        type="text"
        className={classes.input}
        spellCheck="false"
        autoComplete="off"
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...otherProps}
      />
      {value ? (
        <IconButton
          aria-label={translations.resetButtonLabel}
          className={classes.resetIcon}
          onClick={
            /* istanbul ignore next */ () => {
              setValue('');
              onChange('');
            }
          }
          size="large"
        >
          <CloseIcon />
        </IconButton>
      ) : (
        decorativeSearchIcon
      )}
    </div>
  );
}
